<template>
    <div class="available-functions-container-mobile">
        <div class="available-functions">
            <p class="heading">{{ $t('landing-page.functions') }}</p>
            <p class="description">
                {{ $t('landing-page.functions-description') }}
            </p>
        </div>

        <v-row class="function-cards" dense>
            <!-- Colleagues Present -->
            <v-col cols="12" sm="6">
                <v-card class="function-card" elevation="2">
                    <div class="card-content">
                        <v-progress-circular :model-value="colleaguesPresent" :size="50" :width="5" color="blue"
                            bg-color="grey-lighten-3">
                            <small>{{ colleaguesPresent }}%</small>
                        </v-progress-circular>

                        <div class="function-info">
                            <p class="title">{{ $t('landing-page.colleagues') }}</p>
                            <div class="subtitle">
                                <span class="dot blue"></span> {{ $t('landing-page.present') }}&nbsp;&nbsp;
                                <span class="dot gray"></span> {{ $t('landing-page.not-present') }}
                            </div>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <!-- Desks Available -->
            <v-col cols="12" sm="6">
                <v-card class="function-card" elevation="2">
                    <div class="card-content">
                        <v-progress-circular :model-value="desksAvailable" :size="50" :width="5" color="green"
                            bg-color="red">
                            <small>{{ desksAvailable }}%</small>
                        </v-progress-circular>

                        <div class="function-info">
                            <p class="title">{{ $t('landing-page.desks-available') }}</p>
                            <div class="subtitle">
                                <span class="dot green"></span> {{ $t('landing-page.available') }} &nbsp;&nbsp;
                                <span class="dot red"></span> {{ $t('landing-page.booked') }}
                            </div>
                        </div>
                    </div>
                </v-card>
            </v-col>

            <!-- Your Attendance -->
            <v-col cols="12" sm="6">
                <v-card class="function-card" elevation="2">
                    <div class="card-content">
                        <div class="function-info full-width">
                            <p class="title">{{ $t('landing-page.attendance') }}</p>
                            <p class="subtitle">{{ $t('landing-page.attendance-description') }}</p>
                            <v-progress-linear :model-value="20" height="6" color="orange" rounded
                                background-color="grey-lighten-3" class="mt-2" />
                        </div>
                    </div>
                </v-card>
            </v-col>

            <!-- Quick Action -->
            <v-col cols="12" sm="6">
                <v-card class="function-card" elevation="2">
                    <div class="card-content">
                        <div class="quick-icon">
                            <v-icon size="24" color="blue">mdi-flash-outline</v-icon>
                        </div>

                        <div class="function-info">
                            <p class="title" @click="dialog = true">{{ $t('landing-page.quick-actions') }}</p>

                            <p class="subtitle">
                                {{ $t('landing-page.quick-actions-description') }} <span
                                    style="color:#4578f4; font-weight:bold;">&gt;</span>
                            </p>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>
        <v-dialog v-model="dialog" width="300">
            <v-card style="border-radius: 16px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);">
                <v-card-title class="text-h6" style="padding: 16px 20px; font-weight: 600; color: #1e293b;">
                    {{ $t('landing-page.quick-actions') }}
                </v-card-title>
                <v-divider />
                <v-list>
                    <v-list-item v-for="(option, index) in quickActionOptions" :key="index"
                        @click="handleQuickAction(option)"
                        style="padding: 12px 20px; cursor: pointer; transition: background 0.2s;"
                        @mouseover="hovered = index" @mouseleave="hovered = null"
                        :style="hovered === index ? 'background-color: #f1f5f9; border-radius: 8px;' : ''">
                        <v-list-item-title style="font-size: 15px; color: #334155; font-weight: 500;">
                            {{ option.label }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showChatBotDialog" max-width="600">
            <v-card>
                <v-card-title class="d-flex justify-space-between">
                    <span>{{ $t('menu.chatbot-assistant') }}</span>
                    <v-btn icon @click="showChatBotDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

            </v-card>
        </v-dialog>

    </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router'

const colleaguesPresent = ref(80)
const desksAvailable = ref(20)
const { locale, t } = useI18n()
const router = useRouter()
const dialog = ref(false)
const showChatBotDialog = ref(false)
const hovered = ref(null)

const quickActionOptions = [
    {
        label: t('booking.bookings'),
        route: { name: 'UserBookings' }
    },
    {
        label: t('booking.book-now-tab'),
        route: { name: 'BookingPoisList' }
    },
    {
        label: t('menu.ask_help.title'),
        route: null // this opens chatbot
    }
]

const handleQuickAction = (option) => {
    dialog.value = false
    if (option.label === t('menu.ask_help.title')) {
        showChatBotDialog.value = true
        return
    }
    router.push(option.route)
}


const openBooking = () => {
    router.push({ name: 'PoisListView' })
}
</script>

<style scoped>
.available-functions-container-mobile {
    background: #e8eef6;
    padding: 20px;
    margin-top: 20px;
}

.available-functions .heading {
    font-weight: bold;
    font-size: 1.4rem;
}

.available-functions .description {
    color: #718096;
    font-size: 1rem;
    margin-bottom: 20px;
}

.function-cards {
    margin-top: 10px;
}

.function-card {
    border-radius: 12px;
    padding: 16px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    cursor: pointer;
    background: white;
}

.function-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.card-content {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
}

.circle-progress-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
}

.function-info {
    text-align: left;
    flex: 1;
}

.full-width {
    width: 100%;
}

.title {
    font-weight: bold;
    margin: 0;
    font-size: 16px;
}

.subtitle {
    font-size: 13px;
    margin-top: 4px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.dot {
    height: 10px;
    width: 10px;
    border-radius: 50%;
    display: inline-block;
}

.dot.blue {
    background-color: #3b82f6;
    margin-right: 4px;
}

.dot.green {
    background-color: #10b981;
    margin-right: 4px;
}

.dot.gray {
    background-color: #d1d5db;
    margin-right: 4px;
}

.dot.red {
    background-color: #ef4444;
    margin-right: 4px;
}

.quick-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #eef4ff;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 📱 Responsive */
@media (max-width: 770px) {
    .available-functions .heading {
        font-size: 1.3rem;
    }

    .available-functions .description {
        font-size: 0.95rem;
    }

    .title {
        font-size: 15px;
    }

    .subtitle {
        font-size: 12px;
    }
}

@media (max-width: 600px) {
    .function-card {
        padding: 14px;
    }
}

@media (max-width: 480px) {
    .available-functions .heading {
        font-size: 1.2rem;
    }

    .available-functions .description {
        font-size: 0.9rem;
    }
}

@media (max-width: 375px) {
    .available-functions-container-mobile {
        padding: 15px;
    }
}
</style>